import ReactDOM from "react-dom/client";
import "./index.css";
import { ChatWidget } from "./ChatWidget";
import type { WidgetOptions } from "./types/api";
import { checkPlatformConfig } from "./platform/current";
import { setIdentity } from "./utils/identity";
import { setPluginConfig } from "./utils/pluginApi";
import { purgeLegacyIdentityKeys } from "./utils/storageKeys";

declare global {
  interface Window {
    SilfraChatWidget?: {
      init: (options?: WidgetOptions) => void;
    };
  }
}

// Capture the script element immediately (only available at parse time)
const _currentScript = document.currentScript as HTMLScriptElement | null;

function getAssetBaseUrl(script: HTMLScriptElement | null): string {
  if (!script?.src) return "https://silfratech.in/chatbot/";
  const src = script.src;
  return src.substring(0, src.lastIndexOf("/") + 1);
}

function mountWidget(options?: WidgetOptions, assetBaseUrl?: string) {
  // Refuse to mount when the init options contradict the build's platform.
  // A mismatched widget cannot work — the container and checkout panel would
  // come from one platform while the cart hook talks to the other — and it
  // fails deep inside a cart call rather than at startup. Fail here instead,
  // where the message points straight at the misconfiguration.
  const platformError = checkPlatformConfig({
    shopDomain: options?.shopDomain,
  });
  if (platformError) {
    console.error(
      `[MiraQ] Widget not started — platform mismatch.\n${platformError}`,
    );
    return;
  }
  let container = document.getElementById("silfra-chat-widget-root");
  if (!container) {
    container = document.createElement("div");
    container.id = "silfra-chat-widget-root";
    document.body.appendChild(container);
  }

  const apiKey = options?.apiKey || "";
  const apiUrl = options?.apiUrl || "";
  const customerName = options?.customerName || "";

  const customerId = options?.customerId
    ? typeof options.customerId === "string"
      ? parseInt(options.customerId, 10)
      : options.customerId
    : undefined;

  const customerEmail = options?.customerEmail || "";
  const customerRole = options?.customerRole || "";
  const storefrontToken = options?.storefrontToken || "";
  const shopDomain = options?.shopDomain || "";
  const loginUrl = options?.loginUrl || "";
  const brandingLogoUrl = options?.brandingLogoUrl || "";
  const brandingFooterText = options?.brandingFooterText || "";
  const licenseId = options?.licenseId || "";

  // Remove storage keys older builds named after the customer ID / email,
  // before anything in the widget reads storage (utils/storageKeys.ts).
  purgeLegacyIdentityKeys(customerId, customerEmail);

  // Signed identity — the only identity the backend trusts (utils/identity.ts).
  setIdentity(
    options?.identityToken,
    options?.identityExpires,
    options?.wpBaseUrl,
  );

  // Where the plugin's REST routes live and what that plugin build supports.
  setPluginConfig(options?.restBase, options?.features);

  ReactDOM.createRoot(container).render(
    <ChatWidget
      apiKey={apiKey}
      apiUrl={apiUrl}
      customerId={customerId}
      customerEmail={customerEmail}
      customerName={customerName}
      customerRole={customerRole}
      assetBaseUrl={assetBaseUrl}
      wpBaseUrl={options?.wpBaseUrl}
      licenseId={licenseId}
      nonce={options?.nonce}
      nonceExpires={options?.nonceExpires}
      cartToken={options?.cartToken}
      storefrontToken={storefrontToken}
      shopDomain={shopDomain}
      loginUrl={loginUrl}
      brandingLogoUrl={brandingLogoUrl}
      brandingFooterText={brandingFooterText}
    />,
  );
}

window.SilfraChatWidget = {
  init: (options?: WidgetOptions) => {
    const assetBaseUrl = getAssetBaseUrl(_currentScript);
    mountWidget(options, assetBaseUrl);
  },
};

// Local dev convenience: a `vite --mode localdev` build can inject an API
// URL and license ID without a WordPress install. Tree-shaken out of
// production bundles, so there's no risk of a dev license ID shipping.
const _isLocalDevBuild = import.meta.env.MODE === "localdev";
const DEV_API_URL = _isLocalDevBuild
  ? import.meta.env.VITE_DEV_API_URL
  : undefined;
const DEV_LICENSE_ID = _isLocalDevBuild
  ? import.meta.env.VITE_DEV_LICENSE_ID
  : undefined;

(function autoInit() {
  // 1. Safely grab the global config object, defaulting to an empty object
  const config = (window as any).__silfraWidgetConfig || {};

  const script = _currentScript;

  // 2. Extract values: Prefer the global config first, then dataset, then empty strings
  const apiUrl = DEV_API_URL || config.apiUrl || script?.dataset.apiUrl || "";
  const customerId = config.customerId || script?.dataset.customerId || "";
  const customerEmail =
    config.customerEmail || script?.dataset.customerEmail || "";
  const customerName =
    config.customerName || script?.dataset.customerName || "";
  const customerRole =
    config.customerRole || script?.dataset.customerRole || "guest";
  const nonce = config.nonce || script?.dataset.nonce || "";
  // Absolute WP origin, injected by class-widget.php (get_site_url()). Empty on
  // dev builds, where ChatWidget falls back to VITE_WP_BASE_URL / current origin.
  const wpBaseUrl = config.wpBaseUrl || script?.dataset.wpBaseUrl || "";
  // Plugin REST base + feature flags (plugin 1.0.6+). Both absent on older
  // plugin builds, which utils/pluginApi.ts treats as the pre-1.0.6 defaults.
  const restBase = config.restBase || "";
  const features =
    config.features && typeof config.features === "object"
      ? config.features
      : undefined;

  // Tenant identifier from class-widget.php's injected config. No
  // window.location fallback: guessing a tenant is worse than failing
  // loudly, and the backend 400s on an empty header anyway.
  const licenseId =
    DEV_LICENSE_ID || config.licenseId || script?.dataset.licenseId || "";

  const nonceExpires = config.nonceExpires
    ? parseInt(config.nonceExpires, 10)
    : script?.dataset.nonceExpires
      ? parseInt(script.dataset.nonceExpires, 10)
      : 0;

  const cartToken = config.cartToken || script?.dataset.cartToken || "";
  const identityToken = config.identityToken || "";
  const identityExpires = Number(config.identityExpires) || 0;
  const storefrontToken =
    config.storefrontToken || script?.dataset.storefrontToken || "";
  const shopDomain = config.shopDomain || script?.dataset.shopDomain || "";
  const loginUrl = config.loginUrl || script?.dataset.loginUrl || "";
  const brandingLogoUrl =
    config.brandingLogoUrl || script?.dataset.widgetLogo || "";
  const brandingFooterText =
    config.brandingFooterText || script?.dataset.widgetFooterText || "";

  // 3. Fallback for assetBaseUrl in case document.currentScript is null
  // (If script is deferred, _currentScript is null, so we hardcode your production URL as a ultimate fallback)
  const assetBaseUrl = config.assetBaseUrl
    ? config.assetBaseUrl
    : script?.src
      ? getAssetBaseUrl(script)
      : "https://silfratech.in/chatbot-geoffg7/";

  if (!apiUrl) {
    console.warn(
      "[SilfraChatWidget] No API URL found — widget not initialized.",
    );
    return; // Stop execution if we don't at least have the API URL
  }

  // ── Inject CSS ──────────────────────────────────────────────
  if (script?.src) {
    const cssUrl = script.src.replace(/\.js$/, ".css");
    if (!document.querySelector(`link[href="${cssUrl}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssUrl;
      document.head.appendChild(link);
    }
  } else {
    // Ultimate fallback for CSS if script tag isn't detected
    const fallbackCssUrl = `${assetBaseUrl}woocommerce-chat-widget.css`;
    if (!document.querySelector(`link[href="${fallbackCssUrl}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fallbackCssUrl;
      document.head.appendChild(link);
    }
  }
  // ────────────────────────────────────────────────────────────

  mountWidget(
    {
      apiUrl,
      customerId,
      customerEmail,
      customerName,
      customerRole,
      wpBaseUrl,
      restBase,
      features,
      licenseId,
      identityToken,
      identityExpires,
      nonce,
      nonceExpires,
      cartToken,
      storefrontToken,
      shopDomain,
      loginUrl,
      brandingLogoUrl,
      brandingFooterText,
    },
    assetBaseUrl,
  );
})();
